import { useEffect, useMemo, useRef, useState } from "react";
import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import VehicleImage from "@components/ui/VehicleImage";
import ClientVehicleReviewModal from "@components/modals/ClientVehicleReviewModal";
import { clientVehiclesApi } from "@services/api/clientVehicles";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import type { ClientVehicle, VehiclePartyBlock } from "types/client_vehicle";

type FleetFilter = "all" | "fleet" | "non";

const FLEET_TO_PARAM: Record<FleetFilter, boolean | undefined> = {
  all: undefined,
  fleet: true,
  non: false,
};

const PAGE_SIZE = 50;

function partySummary(v: ClientVehicle): { label: string; value: VehiclePartyBlock }[] {
  const out: { label: string; value: VehiclePartyBlock }[] = [];
  if (v.owner?.nom) out.push({ label: "Loueur", value: v.owner });
  if (v.company?.nom) out.push({ label: "Société", value: v.company });
  if (v.user?.nom) out.push({ label: "Conducteur", value: v.user });
  return out;
}

export const ClientVehiclesReview = () => {
  const orgData = useDataStore((s) => s.orgData);
  const orgId = orgData?.id;

  const [query, setQuery] = useState("");
  const [fleet, setFleet] = useState<FleetFilter>("all");
  const [rows, setRows] = useState<ClientVehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [editing, setEditing] = useState<ClientVehicle | null>(null);
  const [saving, setSaving] = useState(false);

  const searching = query.trim().length >= 2;
  const reqId = useRef(0);

  const load = async (opts: { reset: boolean }) => {
    if (!orgId) return;
    const myReq = ++reqId.current;
    setLoading(true);
    try {
      const nextOffset = opts.reset ? 0 : offset;
      let data: ClientVehicle[];
      if (searching) {
        // Plate search across ALL vehicles (Algolia + Firestore fallback backend-side).
        data = await clientVehiclesApi.search({
          plate: query.trim(),
          org_id: orgId,
          is_fleet: FLEET_TO_PARAM[fleet],
          limit: PAGE_SIZE,
        });
      } else {
        data = await clientVehiclesApi.list({
          org_id: orgId,
          is_fleet: FLEET_TO_PARAM[fleet],
          limit: PAGE_SIZE,
          offset: nextOffset,
        });
      }
      if (myReq !== reqId.current) return; // a newer request superseded this one
      if (opts.reset) {
        setRows(data);
        setOffset(searching ? 0 : PAGE_SIZE);
      } else {
        setRows((prev) => [...prev, ...data]);
        setOffset(nextOffset + PAGE_SIZE);
      }
      setHasMore(!searching && data.length === PAGE_SIZE);
    } catch (e) {
      if (myReq === reqId.current) {
        showNotification({ type: "error", message: "Impossible de charger les véhicules." });
      }
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  };

  // Reload on filter/query change (debounced for the search box).
  useEffect(() => {
    if (!orgId) return;
    const t = setTimeout(() => load({ reset: true }), searching ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, fleet, query]);

  const handleSave = async (data: Partial<ClientVehicle>) => {
    if (!editing) return;
    setSaving(true);
    try {
      const updated = await clientVehiclesApi.update(editing.id, data);
      setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      showNotification({ type: "success", message: "Véhicule mis à jour." });
      setEditing(null);
    } catch (e) {
      showNotification({ type: "error", message: "Échec de l'enregistrement." });
    } finally {
      setSaving(false);
    }
  };

  const fleetTabs: { key: FleetFilter; label: string }[] = useMemo(
    () => [
      { key: "all", label: "Tous" },
      { key: "fleet", label: "Flotte / loueur" },
      { key: "non", label: "Particuliers" },
    ],
    [],
  );

  return (
    <div className="flex flex-col w-full h-full p-8 gap-4 overflow-hidden">
      <div className="flex flex-col gap-1">
        <div className="font-semibold text-hx">Véhicules clients</div>
        <div className="text-h4 text-neutral-500">
          Relecture des véhicules migrés — vérifiez et corrigez les parties (propriétaire, société, conducteur)
          avant la bascule des lectures. Les modifications sont enregistrées dans la nouvelle collection uniquement.
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-row items-end gap-3 flex-wrap">
        <div className="w-80">
          <TextField
            label="Rechercher une plaque"
            placeholder="Ex. GP-161-NK"
            value={query}
            onChange={setQuery}
          />
        </div>
        <div className="flex flex-row gap-1 pb-1">
          {fleetTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setFleet(t.key)}
              className={`px-3 py-1.5 rounded-lg text-h4 font-medium transition-colors ${
                fleet === t.key ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex flex-col flex-1 overflow-y-auto rounded-xl border border-neutral-200">
        {rows.length === 0 && !loading && (
          <div className="text-h4 text-neutral-400 text-center py-16">
            {searching ? "Aucun véhicule pour cette plaque." : "Aucun véhicule."}
          </div>
        )}
        {rows.map((v) => {
          const parties = partySummary(v);
          return (
            <div
              key={v.id}
              onClick={() => setEditing(v)}
              className="flex flex-row items-center gap-3 px-4 py-3 border-b border-neutral-100 cursor-pointer hover:bg-neutral-50"
            >
              <div className="w-14 h-10 rounded-lg bg-neutral-50 border border-neutral-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
                <VehicleImage marque={v.marque} modele={v.modele} coloris={v.coloris} mec={v.mec} alt={v.plate} className="w-full h-full" />
              </div>
              <div className="flex flex-col min-w-0 w-48 flex-shrink-0">
                <span className="text-h4 font-semibold text-neutral-800">{v.plate}</span>
                <span className="text-h5 text-neutral-500 truncate">{[v.marque, v.modele].filter(Boolean).join(" ") || "—"}</span>
              </div>
              {v.is_fleet && (
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex-shrink-0">Flotte</span>
              )}
              <div className="flex flex-row flex-wrap gap-x-4 gap-y-0.5 min-w-0 flex-1">
                {parties.length === 0 ? (
                  <span className="text-h5 text-neutral-300">Aucune partie renseignée</span>
                ) : (
                  parties.map((p, i) => (
                    <span key={i} className="text-h5 text-neutral-500 truncate">
                      <span className="text-neutral-400">{p.label} : </span>
                      <span className="text-neutral-700 font-medium">{p.value.nom}</span>
                    </span>
                  ))
                )}
              </div>
              <Icon1 name="right" size={16} />
            </div>
          );
        })}
        {loading && <div className="text-h4 text-neutral-400 text-center py-6">Chargement…</div>}
        {hasMore && !loading && (
          <div className="flex justify-center py-4">
            <SecondaryButton variant="small" onClick={() => load({ reset: false })}>
              Charger plus
            </SecondaryButton>
          </div>
        )}
      </div>

      {editing && (
        <ClientVehicleReviewModal
          vehicle={editing}
          onSave={handleSave}
          onClose={() => setEditing(null)}
          saving={saving}
        />
      )}
    </div>
  );
};
